import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import AppNavBar from './AppNavBar';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const InsightsDashboard = () => {
  const [user] = useAuthState(auth);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      // Check subscription
      const checkAccess = async () => {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const ADMIN_EMAILS = [
              'thomasfairgrieve3@gmail.com',
              'adriandalzell@gmail.com',
              'mark.gomersall@newdur.ac.uk',
            ];
            const isAdmin = ADMIN_EMAILS.includes(user.email);
            const isSubscribed = data?.subscriptionStatus === 'active' ||
              data?.subscriptionTier === 'premium' ||
              data?.subscriptionTier === 'premiumPlus';
  
            if (!isAdmin && !isSubscribed) {
              navigate('/upgrade');
              return;
            }
          } else {
            navigate('/upgrade');
            return;
          }
        } catch (error) {
          console.error('Error checking access:', error);
        }
        fetchInsights();
      };
      checkAccess();
    }
  }, [user]);

  const fetchInsights = async () => {
    try {
      const q = query(collection(db, 'markingInsights'), where('userId', '==', user.uid));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInsights(data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBriefs = () => {
    const briefs = {};
    insights.forEach(insight => {
      if (!briefs[insight.briefId]) {
        briefs[insight.briefId] = {
          briefId: insight.briefId,
          briefName: insight.briefName,
          results: []
        };
      }
      briefs[insight.briefId].results.push(insight);
    });
    return Object.values(briefs);
  };

  const getGradeDistribution = (results) => {
    const dist = { Distinction: 0, Merit: 0, Pass: 0, Fail: 0 };
    results.forEach(r => {
      if (r.overallGrade && dist[r.overallGrade] !== undefined) {
        dist[r.overallGrade]++;
      }
    });
    return dist;
  };

  const getPassRate = (results) => {
    const passing = results.filter(r => r.overallGrade && r.overallGrade !== 'Fail').length;
    return Math.round((passing / results.length) * 100);
  };

  const getCriteriaStats = (results) => {
    const stats = {};
    results.forEach(r => {
      if (!r.criteriaBreakdown) return;
      r.criteriaBreakdown.forEach(criterion => {
        const code = criterion.match(/^[A-Z]\d+/)?.[0];
        if (!code) return;
        if (!stats[code]) stats[code] = { total: 0, achieved: 0 };
        stats[code].total++;
        if (criterion.toLowerCase().includes('achieved') && !criterion.toLowerCase().includes('not achieved')) {
          stats[code].achieved++;
        }
      });
    });
    return Object.entries(stats)
      .sort((a, b) => {
        const order = code => {
          const letter = code[0];
          const num = parseInt(code.slice(1));
          const letterOrder = { P: 0, M: 1, D: 2 };
          return (letterOrder[letter] ?? 99) * 100 + num;
        };
        return order(a[0]) - order(b[0]);
      })
      .map(([code, data]) => ({
        code,
        percentage: Math.round((data.achieved / data.total) * 100),
        achieved: data.achieved,
        total: data.total
      }));
  };

  const getMostMissed = (criteriaStats) => {
    return criteriaStats.reduce((min, c) => c.percentage < min.percentage ? c : min, criteriaStats[0]);
  };

  const getOverallStats = () => {
    if (!insights.length) return null;
    const passing = insights.filter(r => r.overallGrade && r.overallGrade !== 'Fail').length;
    const distinctions = insights.filter(r => r.overallGrade === 'Distinction').length;
    return {
      total: insights.length,
      passRate: Math.round((passing / insights.length) * 100),
      distinctionRate: Math.round((distinctions / insights.length) * 100),
      briefs: getBriefs().length
    };
  };

  const gradeColors = {
    Distinction: '#7F77DD',
    Merit: '#378ADD',
    Pass: '#639922',
    Fail: '#E24B4A'
  };

  const getCriterionColor = (pct) => {
    if (pct >= 80) return '#639922';
    if (pct >= 50) return '#378ADD';
    return '#E24B4A';
  };

  const briefs = getBriefs();
  const overallStats = getOverallStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
        <AppNavBar currentPage="insights" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto mb-4"></div>
            <p className="text-ink-900 text-lg">Loading your insights...</p>
          </div>
        </div>
      </div>
    );
  }

  const EmptyState = () => (
    <div className="text-center py-20">
      <div className="text-6xl mb-4">📊</div>
      <h2 className="text-2xl font-bold text-ink-800 mb-3">No insights yet</h2>
      <p className="text-ink-600 mb-6">Mark some student work to start seeing class performance data here.</p>
      <button
        onClick={() => navigate('/mark-work')}
        className="bg-coral-300 text-white px-6 py-3 rounded-lg hover:bg-coral-500 transition-all duration-300 font-medium"
      >
        Go to Mark Work
      </button>
    </div>
  );

  const BriefCard = ({ brief }) => {
    const dist = getGradeDistribution(brief.results);
    const total = brief.results.length;
    return (
      <div
        onClick={() => setSelectedBrief(brief)}
        className="border-4 border-ink-900 rounded-lg p-4 bg-sand-50 hover:bg-sand-100 cursor-pointer transition-colors"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink-900 text-sm flex-1 mr-4">{brief.briefName.replace(/_/g, ' ')}</h3>
          <span className="text-xs text-ink-500 flex-shrink-0">{total} marked</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden gap-px">
          {Object.entries(dist).map(([grade, count]) =>
            count > 0 ? (
              <div
                key={grade}
                style={{ width: `${(count / total) * 100}%`, background: gradeColors[grade] }}
                title={`${grade}: ${count}`}
              />
            ) : null
          )}
        </div>
        <div className="flex gap-3 mt-2 flex-wrap">
          {Object.entries(dist).map(([grade, count]) =>
            count > 0 ? (
              <span key={grade} style={{ fontSize: '11px', color: gradeColors[grade] }}>
                {grade} {Math.round((count / total) * 100)}%
              </span>
            ) : null
          )}
        </div>
      </div>
    );
  };

  const DrillDown = ({ brief }) => {
    const dist = getGradeDistribution(brief.results);
    const criteriaStats = getCriteriaStats(brief.results);
    const mostMissed = criteriaStats.length ? getMostMissed(criteriaStats) : null;
    const passRate = getPassRate(brief.results);
    const meritRate = Math.round((brief.results.filter(r => r.overallGrade === 'Merit' || r.overallGrade === 'Distinction').length / brief.results.length) * 100);
    const distinctionRate = Math.round((brief.results.filter(r => r.overallGrade === 'Distinction').length / brief.results.length) * 100);

    const chartData = {
      labels: ['Fail', 'Pass', 'Merit', 'Distinction'],
      datasets: [{
        data: [dist.Fail, dist.Pass, dist.Merit, dist.Distinction],
        backgroundColor: ['#E24B4A', '#639922', '#378ADD', '#7F77DD'],
        borderWidth: 0,
        borderRadius: 4
      }]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
    };

    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setSelectedBrief(null)}
            className="text-coral-600 hover:text-coral-800 text-sm underline"
          >
            ← Back to all briefs
          </button>
          <span className="text-ink-500 text-sm">{brief.briefName.replace(/_/g, ' ')} — {brief.results.length} students</span>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Pass rate', value: `${passRate}%`, sub: 'Pass or above' },
            { label: 'Merit rate', value: `${meritRate}%`, sub: 'Merit or above' },
            { label: 'Distinction rate', value: `${distinctionRate}%`, sub: 'All D criteria met' },
            { label: 'Most missed', value: mostMissed ? mostMissed.code : '—', sub: mostMissed ? `${mostMissed.percentage}% achieved` : 'No data' }
          ].map((stat, i) => (
            <div key={i} className="bg-white border border-sand-200 rounded-lg p-4">
              <div className="text-xs text-ink-500 mb-2">{stat.label}</div>
              <div className="text-2xl font-bold text-ink-900">{stat.value}</div>
              <div className="text-xs text-ink-400 mt-1">{stat.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white border-4 border-ink-900 rounded-lg p-5">
            <h3 className="font-semibold text-ink-800 mb-4">Criterion achievement rate</h3>
            <div className="space-y-3">
              {criteriaStats.map(c => (
                <div key={c.code} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-ink-800 w-8">{c.code}</span>
                  <div className="flex-1 bg-sand-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${c.percentage}%`, background: getCriterionColor(c.percentage) }}
                    />
                  </div>
                  <span className="text-sm font-medium w-10 text-right" style={{ color: getCriterionColor(c.percentage) }}>
                    {c.percentage}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border-4 border-ink-900 rounded-lg p-5">
            <h3 className="font-semibold text-ink-800 mb-4">Grade breakdown</h3>
            <div style={{ height: '180px' }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
            <div className="mt-4 p-3 bg-coral-50 border border-coral-200 rounded-lg">
              <p className="text-sm text-coral-900 leading-relaxed">
                {criteriaStats.length > 0 ? (() => {
                  const allP = criteriaStats.filter(c => c.code.startsWith('P'));
                  const allM = criteriaStats.filter(c => c.code.startsWith('M'));
                  const allD = criteriaStats.filter(c => c.code.startsWith('D'));
                  const weakM = allM.filter(c => c.percentage < 70);
                  const weakD = allD.filter(c => c.percentage < 70);
                  const strongP = allP.every(c => c.percentage >= 80);

                  let summary = '';
                  if (strongP) summary += `Pass criteria are well achieved. `;
                  if (weakM.length) summary += `${weakM.map(c => c.code).join(', ')} ${weakM.length === 1 ? 'is' : 'are'} the main barrier to Merit. `;
                  if (weakD.length) summary += `${weakD.map(c => c.code).join(', ')} ${weakD.length === 1 ? 'is' : 'are'} limiting Distinction achievement.`;
                  return summary || 'Review individual criterion data above for teaching focus areas.';
                })() : 'No criterion data available.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="insights" />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
        <h1 className="text-5xl md:text-6xl font-bold text-coral-500 leading-tight mb-8 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">Insights</h1>
          <p className="text-ink-600 mt-1">Class performance across all marked briefs</p>
        </div>

        {insights.length === 0 ? <EmptyState /> : (
          <>
            {!selectedBrief && (
              <>
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Total markings', value: overallStats.total, sub: `across ${overallStats.briefs} brief${overallStats.briefs !== 1 ? 's' : ''}` },
                    { label: 'Overall pass rate', value: `${overallStats.passRate}%`, sub: 'Pass or above' },
                    { label: 'Distinction rate', value: `${overallStats.distinctionRate}%`, sub: 'Across all briefs' },
                    { label: 'Briefs marked', value: overallStats.briefs, sub: 'Total briefs' }
                  ].map((stat, i) => (
                    <div key={i} className="bg-white border border-sand-200 rounded-lg p-4">
                      <div className="text-xs text-ink-500 mb-2">{stat.label}</div>
                      <div className="text-2xl font-bold text-ink-900">{stat.value}</div>
                      <div className="text-xs text-ink-400 mt-1">{stat.sub}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-white border-4 border-ink-900 rounded-lg p-6">
                  <h2 className="font-semibold text-ink-800 mb-1">Briefs</h2>
                  <p className="text-xs text-ink-500 mb-4">Click a brief to drill down into criterion-level data</p>
                  <div className="grid grid-cols-2 gap-4">
                    {briefs.map(brief => <BriefCard key={brief.briefId} brief={brief} />)}
                  </div>
                  <div className="flex gap-4 mt-4 flex-wrap">
                    {Object.entries(gradeColors).map(([grade, color]) => (
                      <span key={grade} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, display: 'inline-block' }}></span>
                        {grade}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {selectedBrief && (
              <div className="bg-white border-4 border-ink-900 rounded-lg p-6">
                <DrillDown brief={selectedBrief} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsDashboard;